/**
 * Ask — questions about finished runs, answered by the read-only analyst.
 *
 * ── What this surface is, and what it refuses to be ────────────────────────────
 *
 * The agent behind it (`target-analyst`) holds five reads over Aurora and S3 and
 * nothing else: no knowledge graph, no literature, no sequence tools, and nothing
 * that writes. It cannot start a sweep, score a hypothesis or look anything up
 * live. Asking it to propose a target gets a refusal and a pointer at the CLI,
 * and that is the design rather than a gap — discovery runs unattended for hours
 * and cannot happen inside a chat turn.
 *
 * So the placeholder and the suggestions below are load-bearing. A blank box next
 * to a ranked leaderboard reads as "ask me anything about herbicide targets", and
 * the first three answers a reader would get are refusals. The examples say what
 * this thing is for before they cost anyone a turn.
 *
 * ── Why the tool trail is shown ────────────────────────────────────────────────
 *
 * Every `tool_call` is named under the answer. Not as a progress spinner — as
 * provenance. This page's whole subject is which claims were measured and which
 * were not, and "the analyst read the review ledger" versus "the analyst read the
 * report document" is the difference between two answers that look identical in
 * prose. It is also the only way a reader can see that an answer came from the
 * board (cumulative, cross-run) rather than from one run's ranking.
 *
 * The analyst's own turns are rendered as markdown (`ui/Markdown`); the reader's
 * are not. `whitespace-pre-wrap` was the deliberate stand-in here and it held for
 * prose, but not for the tables the analyst answers a "show me the ranked list"
 * question with — a GFM pipe table read literally is a `|---|---|` row and cells
 * whose column a reader counts out by hand. A question typed into the box is not
 * markdown and must come back exactly as typed: running it through a parser would
 * eat a lone `*` or an unclosed backtick out of someone's own words.
 *
 * ── Deliberately not here ──────────────────────────────────────────────────────
 *
 * No session list and no history: the thread lives for as long as the tab is
 * open. The durable record of what a sweep found is the leaderboard, the review
 * ledger and the mailed report — a chat log is a reading of those, and persisting
 * it would create a fourth thing that can disagree with them.
 *
 * Reasoning tokens (`thinking: true`) are counted, not shown. They are the
 * model's working, they are long, and on this surface they read as findings.
 */
import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CornerDownLeft, Loader2, Wrench } from 'lucide-react'
import { ApiError } from '../api/leaderboard'
import { streamAsk, type AskEvent } from '../api/ask'
import type { TokenGetter } from '../api/leaderboard'
import { Card, CardBody, CardHeader } from './ui/Card'
import { Button } from './ui/Button'
import { FailedState } from './ui/States'
import { Markdown } from './ui/Markdown'
import { cn } from '../lib/utils'

/**
 * Openers that are actually answerable from the four read surfaces.
 *
 * Kept in step with `../../../agent-house/src/lib/starterPrompts.ts`, which fronts
 * the same agent from the other chat surface. Two copies is the lesser evil: they
 * are copy, they change for editorial reasons, and a shared module would have to
 * cross a repository boundary to save four strings.
 */
const EXAMPLES = [
  'Show me the ranked target list from the most recent sweep, with each one’s tier and how much of the rubric it covers.',
  'What separates the top two targets on that ranking under the scoring rubric?',
  'Which hypotheses did the review harness falsify in the last sweep, and on which axis did each one fail?',
  'Show me the cumulative board and say which entries more than one run has reached.',
]

interface Turn {
  role: 'you' | 'astra'
  text: string
  /**
   * Named reads, in call order, with repeats folded into a count.
   *
   * Folded because the real thing does this: one question about which run ranked
   * the most targets produced ten consecutive `list_runs_and_rankings` calls as
   * the analyst paged the run list, and ten identical chips is noise where two
   * *different* reads is the signal. Counted rather than deduplicated, because
   * "read the run list ten times" and "read it once" are different answers to how
   * much of the record was consulted, and this row is provenance.
   */
  tools: { name: string; n: number }[]
  /** The turn stopped at a bound, so `text` is a partial answer. */
  bound?: string
  error?: string
}

export function AskTab({ getToken }: { getToken: TokenGetter }) {
  /**
   * One thread for the life of the tab.
   *
   * A UUID because the server derives the AgentCore session id from it and that
   * id has a charset and a length floor — see `_session_for` in
   * `api/routes/ask.py`. Generated in the initialiser, not in an effect, so both
   * StrictMode passes agree and the second turn does not land on a new thread.
   */
  const [threadId] = useState(() => crypto.randomUUID())
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')
  const [pending, setPending] = useState(false)
  /** Set only for a failed handshake; a mid-stream failure is a turn's `error`. */
  const [fatal, setFatal] = useState<string | null>(null)
  const [unauthorized, setUnauthorized] = useState(false)

  const abort = useRef<AbortController | null>(null)
  const tail = useRef<HTMLDivElement | null>(null)

  // Abort in flight work on unmount. Switching tabs unmounts this, and a stream
  // left reading into a dead component logs a React warning on every token.
  useEffect(() => () => abort.current?.abort(), [])

  useEffect(() => {
    tail.current?.scrollIntoView({ block: 'end' })
  }, [turns, pending])

  const send = async (question: string) => {
    const text = question.trim()
    if (!text || pending) return
    setDraft('')
    setFatal(null)
    setPending(true)
    setTurns((prev) => [
      ...prev,
      { role: 'you', text, tools: [] },
      { role: 'astra', text: '', tools: [] },
    ])

    // Mutated in place and written back through `setTurns` on each event: the
    // answer is the accumulation of hundreds of `token` events, and reading the
    // previous state out of the setter for every one of them is how the last
    // token wins a race with the first.
    const answer: Turn = { role: 'astra', text: '', tools: [] }
    const put = () => setTurns((prev) => [...prev.slice(0, -1), { ...answer }])

    const controller = new AbortController()
    abort.current = controller

    const onEvent = (event: AskEvent) => {
      switch (event.type) {
        case 'token': {
          const e = event as Extract<AskEvent, { type: 'token' }>
          // Reasoning is the model's working, not the answer.
          if (e.thinking) return
          answer.text += e.content
          put()
          break
        }
        case 'tool_call': {
          const e = event as Extract<AskEvent, { type: 'tool_call' }>
          // Indexed rather than `.at(-1)`: this project's `lib` is below es2022.
          const last = answer.tools[answer.tools.length - 1]
          // A fresh array and a fresh entry rather than an in-place bump:
          // `put()` spreads `answer` shallowly, so a count incremented inside
          // the array React already rendered would not show up.
          answer.tools =
            last?.name === e.name
              ? [...answer.tools.slice(0, -1), { name: e.name, n: last.n + 1 }]
              : [...answer.tools, { name: e.name, n: 1 }]
          put()
          break
        }
        case 'bound': {
          const e = event as Extract<AskEvent, { type: 'bound' }>
          answer.bound =
            e.message || 'The turn stopped at a limit — this answer is partial.'
          put()
          break
        }
        case 'error': {
          const e = event as Extract<AskEvent, { type: 'error' }>
          answer.error = e.message
          put()
          break
        }
        default:
          // `status`, `todos`, `done` and anything a later version adds. `done`
          // needs no handling: `streamAsk` resolves when the stream closes,
          // which is what clears `pending`.
          break
      }
    }

    try {
      await streamAsk({
        message: text,
        threadId,
        getToken,
        signal: controller.signal,
        onEvent,
      })
      // An empty answer with no error is a real outcome worth naming rather than
      // leaving as a blank bubble — it is what a turn that produced only
      // reasoning looks like.
      if (!answer.text && !answer.error) {
        answer.error = 'The analyst returned no answer. Try asking again.'
        put()
      }
    } catch (err) {
      if (controller.signal.aborted) return
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        // Same contract as every other call here: the shell shows the sign-in
        // gate, this panel does not try to explain auth.
        setUnauthorized(true)
      }
      setFatal(err instanceof Error ? err.message : String(err))
      // Drop the empty answer bubble — the banner carries the reason, and a
      // bubble that never filled in reads as the analyst having said nothing.
      setTurns((prev) => prev.slice(0, -1))
    } finally {
      abort.current = null
      setPending(false)
    }
  }

  if (unauthorized) {
    return (
      <FailedState message="Your session has expired. Reload the page to sign in again." />
    )
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          eyebrow="ASK"
          title="Questions about finished runs"
          description={
            <>
              Answered from the leaderboard, the cumulative board, the review
              ledger and the generated reports — the four durable records a
              completed sweep leaves behind. It reads them and nothing else: no
              knowledge graph, no literature, no sequence work, and no way to
              start a sweep. Where a value was never measured it says so and
              names the gap rather than filling it in.
            </>
          }
        />

        <CardBody className="space-y-4">
          {turns.length === 0 && (
            <div className="space-y-2">
              <p className="text-xs text-slate-500">
                Questions this can answer:
              </p>
              {EXAMPLES.map((example) => (
                <button
                  key={example}
                  type="button"
                  onClick={() => send(example)}
                  disabled={pending}
                  className="block w-full rounded-lg border border-slate-800 bg-slate-950/60 px-3 py-2 text-left text-xs leading-relaxed text-slate-300 transition-colors hover:border-slate-700 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50"
                >
                  {example}
                </button>
              ))}
            </div>
          )}

          {turns.map((turn, i) => (
            <div
              key={i}
              className={cn(
                'rounded-lg px-3 py-2 text-sm leading-relaxed',
                turn.role === 'you'
                  ? 'border border-slate-800 bg-slate-950/60 text-slate-300'
                  : 'text-slate-200',
              )}
            >
              <p className="mb-1 font-display text-2xs font-semibold uppercase tracking-eyebrow text-slate-500">
                {turn.role === 'you' ? 'You' : 'ASTRA'}
              </p>
              {/* The analyst's answers carry accessions, tier letters, coverage
                  fractions and null-vs-zero distinctions, and every one of those
                  survives the markdown pass — `remark-gfm` only reshapes block
                  structure, and inline code keeps a `null` a value rather than a
                  word. The reader's own text stays verbatim: it was never
                  markdown, so parsing it could only lose characters. */}
              {turn.role === 'you' ? (
                <p className="whitespace-pre-wrap break-words">{turn.text}</p>
              ) : (
                <Markdown text={turn.text} />
              )}

              {turn.tools.length > 0 && (
                <p className="mt-2 flex flex-wrap items-center gap-1.5 text-2xs text-slate-500">
                  <Wrench className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="sr-only">Read from:</span>
                  {turn.tools.map((tool, j) => (
                    <code
                      key={`${tool.name}-${j}`}
                      className="rounded bg-slate-800/70 px-1 py-0.5 font-mono"
                    >
                      {tool.name}
                      {tool.n > 1 && (
                        <span className="text-slate-400"> ×{tool.n}</span>
                      )}
                    </code>
                  ))}
                </p>
              )}

              {turn.bound && (
                <p className="mt-2 flex items-start gap-1.5 text-2xs text-amber-400/90">
                  <AlertTriangle
                    className="mt-0.5 h-3 w-3 shrink-0"
                    aria-hidden="true"
                  />
                  {turn.bound}
                </p>
              )}

              {turn.error && (
                <p
                  role="alert"
                  className="mt-2 break-words text-2xs leading-relaxed text-red-300"
                >
                  {turn.error}
                </p>
              )}
            </div>
          ))}

          {pending && (
            <p
              className="flex items-center gap-2 text-xs text-slate-500"
              aria-live="polite"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              Reading the leaderboard…
            </p>
          )}

          <div ref={tail} />

          {fatal && <FailedState message={fatal} />}

          <form
            onSubmit={(event) => {
              event.preventDefault()
              void send(draft)
            }}
            className="space-y-2"
          >
            <label className="sr-only" htmlFor="ask-input">
              Your question
            </label>
            <textarea
              id="ask-input"
              rows={3}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                // Enter sends, Shift+Enter breaks the line. A question that
                // names three accessions wants line breaks; a chat box that
                // needs a mouse to send does not.
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault()
                  void send(draft)
                }
              }}
              placeholder="e.g. why is the target ranked third below the two above it?"
              className="w-full resize-y rounded-lg border border-slate-800 bg-slate-950 px-3 py-2 text-sm text-slate-200 placeholder:text-slate-600 focus-visible:border-brand-600 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-600"
            />
            <div className="flex items-center justify-between gap-3">
              <p className="text-2xs text-slate-500">
                Read-only. It cannot start a sweep — use{' '}
                <code className="font-mono text-slate-400">
                  target-discovery run --autonomous
                </code>{' '}
                for that.
              </p>
              <Button type="submit" size="sm" disabled={pending || !draft.trim()}>
                <CornerDownLeft className="h-3.5 w-3.5" />
                Ask
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  )
}
